import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';

import { StatusBadge } from '@/components/ui/StatusBadge';
import { useAccountClosure } from '@/hooks/admin/useAccountClosure';
import { ClosurePanel } from '@/pages/admin/customer-detail/ClosureTab';

/**
 * /admin/customers/qbo/:qboId/closure — the account-closure workflow for ANY
 * QuickBooks customer, whether or not they are on the portal (Sky, 2026-09-29:
 * "if they are not on the portal I'd like the same check system to run").
 *
 * Linked from Refractor's INACTIVATE button. A customer who DOES have a portal
 * row is sent to their customer-detail Closure tab instead, so there is one
 * closure per customer and the two entry points can never disagree.
 */
export default function AdminCustomerClosure() {
  const { qboId } = useParams<{ qboId: string }>();
  const { data, isLoading, error } = useAccountClosure({ qboId });

  if (isLoading) {
    return (
      <div className="px-1 py-10 text-center text-sm text-brix-muted">
        <Loader2 size={16} className="animate-spin inline-block mr-2" />
        Loading customer…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="rounded-lg border border-brix-warning/30 bg-brix-warning/5 px-3 py-2 text-sm text-brix-warning">
        {error instanceof Error ? error.message : 'Could not load this customer.'}
      </div>
    );
  }

  const c = data.customer;
  const portalId = c.id;

  return (
    <div className="space-y-4">
      <Link to="/admin/customers" className="inline-flex items-center gap-1 text-xs text-brix-muted no-underline">
        <ArrowLeft size={12} /> Customers
      </Link>
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-display text-xl font-semibold text-brix-ink">{c.name}</h1>
        <span className="text-xs text-brix-muted">QuickBooks #{c.qbo_customer_id}</span>
        {portalId ? (
          <StatusBadge tone="info">On the portal</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">Not on the portal</StatusBadge>
        )}
        {!c.active && <StatusBadge tone="neutral">Inactive</StatusBadge>}
      </div>
      {portalId && (
        <p className="text-sm text-brix-muted">
          This customer is on the portal — the same closure also lives on{' '}
          <Link to={`/admin/customers/${portalId}/closure`} className="text-brix-primary font-semibold no-underline">
            their customer page
          </Link>
          .
        </p>
      )}
      <ClosurePanel subject={{ qboId }} name={c.name} />
    </div>
  );
}
